/**
 * Stato condiviso.
 *
 * Regola di §1.ter: **lo stato di un'azione vive in un posto solo.** Le card in
 * chat e le righe nello storico leggono dalla stessa mappa, alimentata dagli
 * eventi `action_update`. Così una card non può mai mostrare uno stato diverso
 * da quello che lo storico mostra due schermate più in là.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AccountView, ActionView, AutonomyView } from "@otto/shared";
import { api, ApiError, getToken, onSessioneScaduta, setToken } from "./api.ts";
import { openEventStream } from "./stream.ts";

export interface UtenteView {
  id: string;
  email: string;
  nome: string | null;
  ruolo: "proprietario" | "collaboratore";
  creatoIl: string | null;
  /** Non ha ancora accettato i Termini in vigore: si chiedono prima di tutto. */
  terminiDaAccettare: boolean;
}

/** Un avviso passato: resta nella campanella anche dopo che l'ovale è sparito. */
export interface Notifica {
  id: number;
  testo: string;
  /** Quando è arrivato, in millisecondi. */
  quando: number;
  /**
   * Dove porta. Un avviso che chiede qualcosa all'host deve avere dove
   * andare: «Ho 2 risposte da farti controllare» senza un tasto è una
   * notifica, e le notifiche si imparano a ignorare.
   */
  azione?: AzioneNotifica;
}

export interface AzioneNotifica {
  etichetta: string;
  esegui: () => void;
}

export interface OpzioniNotifica {
  azione?: AzioneNotifica;
  /**
   * Entra nella campanella senza ovale e senza contare fra le nuove: per le
   * cose già annunciate una volta, che restano lì finché non le si guarda.
   */
  silenziosa?: boolean;
}

/** Quanti se ne tengono: oltre, i più vecchi escono. */
const NOTIFICHE_MAX = 30;
/** Quanto resta a schermo l'ovale. */
const DURATA_OVALE_MS = 5_000;

interface StoreValue {
  ready: boolean;
  /** `false` quando non c'è una sessione: si mostra la schermata di accesso. */
  autenticato: boolean;
  account: AccountView | null;
  /** Chi è entrato: nome, email, ruolo. */
  utente: UtenteView | null;
  autonomy: AutonomyView[];
  actions: Map<string, ActionView>;
  error: string | null;

  getAction(id: string): ActionView | undefined;
  refreshAction(id: string): Promise<void>;
  refreshActions(): Promise<void>;
  refreshAccount(): Promise<AccountView>;
  patchAction(action: ActionView): void;
  notify(message: string, opzioni?: OpzioniNotifica): void;
  /** L'avviso a schermo adesso, nell'ovale accanto alla campanella. */
  toast: string | null;
  /** La notifica dell'ovale, con la sua eventuale azione. */
  notificaInVista: Notifica | null;
  /** Tutti gli avvisi della sessione, dal più recente. */
  notifiche: Notifica[];
  /** Quanti sono arrivati dall'ultima volta che si è aperta la campanella. */
  nonLette: number;
  segnaLette(): void;
  svuotaNotifiche(): void;

  /**
   * Comando preparato da un'altra vista (per esempio scegliendo un intervallo
   * sul calendario). La chat lo raccoglie e lo mette nell'input: preparare il
   * comando non è eseguirlo, l'approvazione resta dov'era.
   */
  promptDraft: string | null;
  setPromptDraft(text: string | null): void;
}

/** Esportato per i test: permette di iniettare uno stato senza toccare la rete. */
export const StoreContext = createContext<StoreValue | null>(null);
export type { StoreValue };

export function StoreProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [autenticato, setAutenticato] = useState(() => getToken() != null);
  const [account, setAccount] = useState<AccountView | null>(null);
  const [utente, setUtente] = useState<UtenteView | null>(null);
  const [autonomy, setAutonomy] = useState<AutonomyView[]>([]);
  const [actions, setActions] = useState<Map<string, ActionView>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [notificaInVista, setNotificaInVista] = useState<Notifica | null>(null);
  const [notifiche, setNotifiche] = useState<Notifica[]>([]);
  const [nonLette, setNonLette] = useState(0);
  const prossimaNotifica = useRef(1);
  const [promptDraft, setPromptDraft] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patchAction = useCallback((action: ActionView) => {
    setActions((previous) => new Map(previous).set(action.id, action));
  }, []);

  const refreshAction = useCallback(
    async (id: string) => {
      try {
        const { action } = await api.action(id);
        patchAction(action);
      } catch {
        /* L'azione può essere sparita: non è un errore da mostrare. */
      }
    },
    [patchAction],
  );

  const refreshActions = useCallback(async () => {
    // Cento invece di trenta: le card vivono dentro le conversazioni, e una
    // conversazione di due settimane fa aveva la sua azione fuori dalla
    // finestra — la card mostrava "Approva" su una cosa già eseguita. Quando
    // nemmeno cento bastano, `ProposalCard` va a prendersi la sua.
    const { actions: list } = await api.actions(undefined, 100);
    setActions(new Map(list.map((action) => [action.id, action])));
  }, []);

  const refreshAccount = useCallback(async () => {
    const me = await api.me();
    setAccount(me.account);
    setUtente(me.user);
    setAutonomy(me.autonomy);
    return me.account;
  }, []);

  /**
   * La sessione è finita mentre si usava l'app.
   *
   * Prima lo gestiva solo l'avvio: dopo una revoca ogni tap dava un toast
   * rosso, e lo stream continuava a riconnettersi con un token morto. Adesso
   * si torna all'accesso, una volta sola.
   */
  useEffect(
    () =>
      onSessioneScaduta(() => {
        setAutenticato(false);
        setReady(true);
        setError(null);
      }),
    [],
  );

  const notify = useCallback(
    (message: string, opzioni: OpzioniNotifica = {}) => {
      const notifica: Notifica = {
        id: prossimaNotifica.current++,
        testo: message,
        quando: Date.now(),
        ...(opzioni.azione ? { azione: opzioni.azione } : {}),
      };
      setNotifiche((prima) => [notifica, ...prima].slice(0, NOTIFICHE_MAX));
      if (opzioni.silenziosa) return;
      setToast(message);
      setNotificaInVista(notifica);
      setNonLette((n) => n + 1);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => {
        setToast(null);
        setNotificaInVista(null);
      }, DURATA_OVALE_MS);
    },
    [],
  );

  const segnaLette = useCallback(() => {
    setNonLette(0);
    // Aprendo la campanella l'ovale non serve più: l'avviso è lì dentro.
    setToast(null);
    setNotificaInVista(null);
  }, []);

  const svuotaNotifiche = useCallback(() => {
    setNotifiche([]);
    setNonLette(0);
  }, []);

  // ── Avvio: sessione, dati, stream ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        // Non si entra più da soli: senza token si mostra l'accesso, e questo
        // effetto riparte quando la sessione arriva.
        if (!getToken()) {
          if (!cancelled) {
            setAutenticato(false);
            setReady(true);
          }
          return;
        }

        // Prima l'account: se l'abbonamento è fermo, il resto dell'API
        // risponde 402 e non c'è altro da caricare — l'app mostra «Riattiva».
        const account = await refreshAccount();
        if (!account.bloccatoPerAbbonamento) await refreshActions();
        if (!cancelled) {
          setAutenticato(true);
          setReady(true);
        }
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          // Sessione scaduta o revocata: si torna all'accesso, senza errore
          // rosso — non è un guasto, è solo il momento di rientrare.
          setToken(null);
          setAutenticato(false);
          setReady(true);
          return;
        } else {
          setError(
            caught instanceof ApiError
              ? caught.message
              : "Non riesco a raggiungere il server. È avviato? (`npm run dev`)",
          );
        }
        setReady(true);
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [refreshAccount, refreshActions]);

  // ── Aggiornamenti live: è questo che fa "vivere" le card ──────────────
  const bloccato = account?.bloccatoPerAbbonamento === true;
  useEffect(() => {
    if (!ready || error || bloccato) return;

    return openEventStream(
      "/api/stream",
      (event) => {
        if (event.type !== "action_update") return;
        // La vista arriva intera, quindi si sostituisce intera: niente più
        // campi vecchi accanto a campi nuovi. È anche il motivo per cui
        // un'azione mai vista prima non va più a prendersi da sola — ce
        // l'abbiamo già davanti.
        patchAction(event.action);
      },
      // Appena (ri)collegati si rilegge tutto: lo stream racconta solo i
      // cambiamenti, e quelli successi mentre la connessione era giù non li
      // rimanda nessuno. Senza questa riga bastava un wifi ballerino subito
      // dopo un'approvazione per lasciare la card su «la sto scrivendo…» a
      // tempo indefinito, con la modifica già arrivata sul gestionale.
      () => {
        void refreshActions();
      },
    );
  }, [ready, error, bloccato, patchAction, refreshActions]);

  const value = useMemo<StoreValue>(
    () => ({
      ready,
      autenticato,
      account,
      utente,
      autonomy,
      actions,
      error,
      toast,
      notificaInVista,
      notifiche,
      nonLette,
      segnaLette,
      svuotaNotifiche,
      promptDraft,
      setPromptDraft,
      getAction: (id) => actions.get(id),
      refreshAction,
      refreshActions,
      refreshAccount,
      patchAction,
      notify,
    }),
    [
      ready,
      autenticato,
      account,
      utente,
      autonomy,
      actions,
      error,
      toast,
      notificaInVista,
      notifiche,
      nonLette,
      segnaLette,
      svuotaNotifiche,
      promptDraft,
      refreshAction,
      refreshActions,
      refreshAccount,
      patchAction,
      notify,
    ],
  );

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error("useStore fuori da StoreProvider");
  return value;
}
